import DistBars from '../components/DistBars';
import React, { useEffect, useState, useCallback } from 'react';

// 🗣️ Narrációk — narration_samples böngésző (read-only)
const card = { background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 12, padding: 16 };
const title = { fontSize: 13, color: '#8b8fa3', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 };
const inp = { background: '#0a0d14', border: '1px solid #2a2e3a', borderRadius: 6, padding: '8px 10px', color: '#e4e6eb', fontSize: 13, boxSizing: 'border-box' };
const btn = { padding: '8px 16px', borderRadius: 6, border: 'none', background: '#00d4ff', color: '#0a0d14', cursor: 'pointer', fontWeight: 600, fontSize: 12 };

const OPENERS = ['arrive', 'motion', 'detail', 'scene', 'hook', 'null'];
const WIKIQA = ['poi', 'city', 'none'];
const GEO = ['direct', 'nearby', 'null'];
const PROMPTS = ['rich', 'short'];

// Magyar címkék — az API-nak az eredeti (tárolt) érték megy, csak a megjelenítés magyar
const HU = {
  arrive: 'érkezés', motion: 'mozgás', detail: 'részlet', scene: 'jelenet', hook: 'felütés',
  poi: 'POI', city: 'város', none: 'nincs', null: 'nincs',
  direct: 'pontos', nearby: 'közeli',
  rich: 'gazdag',
  hungarian: 'magyar', english: 'angol', german: 'német', french: 'francia', italian: 'olasz',
  spanish: 'spanyol', polish: 'lengyel', chinese: 'kínai', japanese: 'japán', portuguese: 'portugál',
  korean: 'koreai', slovak: 'szlovák', romanian: 'román', slovenian: 'szlovén',
  adult: 'felnőtt', family: 'családos', kids: 'gyerekes', child: 'gyerek', senior: 'idős',
  walking: 'séta', driving: 'autós', cycling: 'kerékpár',
  full: 'teljes', short: 'rövid', medium: 'közepes', long: 'hosszú',
  wikipedia: 'Wikipédia', wikidata: 'Wikidata', wikivoyage: 'Wikivoyage', related: 'kapcsolt',
};
const hu = (v) => {
  if (v === null || v === undefined || v === '') return 'nincs';
  const k = String(v).toLowerCase();
  return HU[k] !== undefined ? HU[k] : v;
};

const OPENER_COLORS = { arrive: '#22c55e', motion: '#00d4ff', detail: '#a78bfa', scene: '#f59e0b', hook: '#f43f5e', null: '#8b8fa3' };
const WIKI_COLORS = { poi: '#22c55e', city: '#f59e0b', none: '#8b8fa3', null: '#8b8fa3' };
// A prompt útja: 'rich' = gazdag (forrással), 'short' = rövid. A régi mintákban nincs.
const PROMPT_COLORS = { rich: '#22c55e', short: '#8b8fa3', null: '#5b6270' };
const promptLabel = (k) => (k === 'null' ? 'régi minta' : hu(k));
const SRC_COLORS = { wikipedia: '#e4e6eb', wikidata: '#00d4ff', wikivoyage: '#f59e0b', related: '#a78bfa' };
// Tény-karakterszám a részletnézetben. A hiányzó mező régi mintát jelent;
//   a null jelentése mezőnként más (a wikiQAChars-nál: a lekérdezés nem futott le).
const factsText = (d, key, nullText = 'nincs adat') => {
  if (!d || !Object.prototype.hasOwnProperty.call(d, key)) return 'nincs adat';
  const v = d[key];
  if (v === null || v === undefined) return nullText;
  return `${v} kar.`;
};
// ── Koordináta-utak (zero-source geo-blokk, narration_samples PR #104) ──
// A B1 (Wikidata) kimenete — a „B1 ok" szűrő értékei. A backend geoB1Outcome()
//   ugyanezeket a kódokat adja: legacy / notrun / hit / error / geoWdReject-kód.
const GEO_REJECT_LABELS = {
  'no-hits': 'nincs item 150 m-en belül',
  'all-denied': 'csak tiltott típus',
  'token-too-far': 'névegyezés, de >100 m',
  'sole-too-far': 'egy jelölt, >40 m',
  ambiguous: 'több jelölt, nincs névegyezés',
  'cached-none': 'régi negatív cache',
};
const GEO_B1_OPTIONS = [
  ['hit', 'találat'],
  ...Object.entries(GEO_REJECT_LABELS),
  ['error', 'hiba'],
  ['notrun', 'nem futott'],
  ['legacy', 'régi minta'],
];
const GEO_VIA_LABELS = { token: 'névegyezés', sole: 'egyetlen jelölt', cache: 'cache' };
const hasKey = (d, k) => !!d && Object.prototype.hasOwnProperty.call(d, k);
const present = (d, k) => hasKey(d, k) && d[k] !== null && d[k] !== undefined && d[k] !== '';

function GeoPaths({ d }) {
  const row = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 12, color: '#c9ccd6', marginBottom: 12 };
  const head = <span style={{ color: '#8b8fa3', fontWeight: 600 }}>Koordináta-utak:</span>;
  const dim = { color: '#8b8fa3' };
  const sep = <span style={dim}>·</span>;
  if (!hasKey(d, 'geoRan')) return <div style={row}>{head}<span style={dim}>nincs adat</span></div>;
  if (d.geoRan === false) return <div style={row}>{head}<span>nem futott (volt névalapú forrás)</span></div>;

  // B1 (Wikidata)
  let b1;
  if (present(d, 'geoWd')) {
    const q = String(d.geoWd);
    const via = present(d, 'geoWdVia') ? (GEO_VIA_LABELS[d.geoWdVia] || String(d.geoWdVia)) : 'nincs adat';
    const dist = typeof d.geoWdDist === 'number' ? `${Math.round(d.geoWdDist)} m` : 'táv: nincs adat';
    b1 = (
      <span>
        {/^Q\d+$/.test(q)
          ? <a href={`https://www.wikidata.org/wiki/${q}`} target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff' }}>{q}</a>
          : q}
        {' · '}<span title={present(d, 'geoWdVia') ? `geoWdVia: ${d.geoWdVia}` : undefined}>{via}</span>{' · '}{dist}
      </span>
    );
  } else if (present(d, 'geoWdReject')) {
    const r = String(d.geoWdReject);
    const isErr = /^error/i.test(r);
    const label = isErr ? 'hiba' : (GEO_REJECT_LABELS[r] || r);
    b1 = <span title={`geoWdReject: ${r}`} style={{ color: isErr ? '#f43f5e' : '#f59e0b', cursor: 'help' }}>{label}</span>;
  } else if (hasKey(d, 'geoWd') || hasKey(d, 'geoWdReject')) {
    b1 = <span style={dim}>nincs találat, ok nélkül</span>;
  } else {
    b1 = <span style={dim}>nincs adat</span>;
  }
  const nearest = !hasKey(d, 'geoWdNearest') ? <span style={dim}>nincs adat</span>
    : present(d, 'geoWdNearest') ? <span>{String(d.geoWdNearest)}</span> : <span style={dim}>nincs</span>;
  const mon = !hasKey(d, 'geoMon') ? <span style={dim}>nincs adat</span>
    : present(d, 'geoMon') ? <span>{String(d.geoMon)}</span> : <span style={dim}>nincs találat</span>;
  return (
    <div style={row}>
      {head}
      <span title="B1: Wikidata-item a koordináta közelében"><span style={dim}>B1:</span> {b1}</span>{sep}
      <span title="A legközelebbi Wikidata-jelölt, ahogy a minta tárolja"><span style={dim}>legközelebbi:</span> {nearest}</span>{sep}
      <span title="Magyar műemlék-nyilvántartás (törzsszám)"><span style={dim}>műemlék:</span> {mon}</span>
    </div>
  );
}

const srcList = (x) => [x.wikidata && 'wikidata', x.wikivoyage && 'wikivoyage', x.related && 'related'].filter(Boolean);

const fmtTimeS = (iso) => iso
  ? new Intl.DateTimeFormat('hu-HU', { timeZone: 'Europe/Budapest', hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 }).format(new Date(iso))
  : '—';
const SEV_COLORS = { ERROR: '#f43f5e', WARNING: '#f59e0b', INFO: '#00d4ff', DEBUG: '#8b8fa3', DEFAULT: '#8b8fa3', NOTICE: '#22c55e' };
const fmtTime = (ms) => ms
  ? new Intl.DateTimeFormat('hu-HU', { timeZone: 'Europe/Budapest', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(ms))
  : '—';

function Badge({ text, color, title }) {
  if (!text) return null;
  return (
    <span title={title} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color, background: `${color}1a`, border: `1px solid ${color}40`, marginRight: 4, cursor: title ? 'help' : undefined }}>
      {text}
    </span>
  );
}

// A nyitó stílus / wiki forrás / prompt út megoszlása — közös komponens (components/DistBars).
const Dist = ({ label, dist, colors, total, labelOf = hu }) => (
  <DistBars label={label} dist={dist} colors={colors} total={total} labelOf={labelOf} />
);

export default function Narrations() {
  // Szűrők
  const [lang, setLang] = useState('');
  const [profile, setProfile] = useState('');
  const [openerStyle, setOpenerStyle] = useState('');
  const [wikiQA, setWikiQA] = useState('');
  const [promptPath, setPromptPath] = useState('');
  const [geo, setGeo] = useState('');
  const [geoB1, setGeoB1] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [stats, setStats] = useState(null);
  const [langOptions, setLangOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingTrace, setExportingTrace] = useState(false);
  const [exportingFull, setExportingFull] = useState(false);
  const [error, setError] = useState(null);

  // Részletnézet
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [trace, setTrace] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);

  const params = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    if (lang) p.set('lang', lang);
    if (profile) p.set('profile', profile);
    if (openerStyle) p.set('openerStyle', openerStyle);
    if (wikiQA) p.set('wikiQA', wikiQA);
    if (promptPath) p.set('promptPath', promptPath);
    if (geo) p.set('geo', geo);
    if (geoB1) p.set('geoB1', geoB1);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (q) p.set('q', q);
    Object.entries(extra).forEach(([k, v]) => v != null && p.set(k, v));
    return p.toString();
  }, [lang, profile, openerStyle, wikiQA, promptPath, geo, geoB1, from, to, q]);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setSelected(null); setDetail(null);
    try {
      const [listRes, statsRes] = await Promise.allSettled([
        fetch(`/api/narrations?${params({ limit: 50 })}`, { credentials: 'include' }),
        fetch(`/api/narrations/stats?${params()}`, { credentials: 'include' }),
      ]);
      if (listRes.status === 'fulfilled' && listRes.value.ok) {
        const d = await listRes.value.json();
        setItems(d.items || []);
        setNextCursor(d.nextCursor);
        setHasMore(!!d.hasMore);
      } else {
        throw new Error(listRes.status === 'fulfilled' ? `HTTP ${listRes.value.status}` : listRes.reason?.message || 'Hálózati hiba');
      }
      if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
        const s = await statsRes.value.json();
        setStats(s);
        setLangOptions((prev) => Array.from(new Set([...prev, ...Object.keys(s.langDist || {})])).sort());
      } else {
        setStats(null);
      }
    } catch (e) { setError(e.message); setItems([]); setStats(null); }
    finally { setLoading(false); }
  }, [params]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await fetch(`/api/narrations?${params({ limit: 50, cursor: nextCursor })}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setItems((prev) => [...prev, ...(d.items || [])]);
      setNextCursor(d.nextCursor);
      setHasMore(!!d.hasMore);
    } catch (e) { setError(e.message); }
    finally { setLoadingMore(false); }
  };

  const exportXlsx = async () => {
    setExporting(true); setError(null);
    try {
      const res = await fetch(`/api/narrations/export?${params()}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `narraciok_${from || stamp}${to && to !== from ? '_' + to : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setExporting(false); }
  };

  const exportTrace = async () => {
    if (!from) { setError('A trace exporthoz add meg legalább a kezdő dátumot.'); return; }
    setExportingTrace(true); setError(null);
    try {
      const res = await fetch(`/api/narrations/trace-export?${params()}`, { credentials: 'include' });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j.error) msg = j.error; } catch (_) {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `narracio_trace_${from}${to && to !== from ? '_' + to : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setExportingTrace(false); }
  };

  // 🆕 Egyesített "Elemző export" — egy .xlsx: Összefoglaló + Narrációk + POI minőség + Napi + Trace
  const exportFull = async () => {
    setExportingFull(true); setError(null);
    try {
      const res = await fetch(`/api/narrations/full-export?${params()}`, { credentials: 'include' });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j.error) msg = j.error; } catch (_) {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `elemzo_export_${from || stamp}${to && to !== from ? '_' + to : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setExportingFull(false); }
  };

  useEffect(() => {
    load();
    const qid = new URLSearchParams(window.location.search).get('id');
    if (qid) openDetail({ id: qid });
  }, []);

  const openDetail = async (item) => {
    setSelected(item.id);
    setDetail(null);
    setTrace(null);
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/narrations/${encodeURIComponent(item.id)}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDetail(await res.json());
    } catch (e) { setDetail({ error: e.message }); }
    finally { setDetailLoading(false); }
  };

  const loadTrace = async () => {
    if (!selected) return;
    setTraceLoading(true);
    try {
      const res = await fetch(`/api/narrations/${encodeURIComponent(selected)}/trace`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTrace(await res.json());
    } catch (e) { setTrace({ error: e.message }); }
    finally { setTraceLoading(false); }
  };

  const sel = { ...inp, minWidth: 110 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ margin: 0, color: '#e4e6eb', fontSize: 20 }}>🗣️ Narrációk</h2>

      {/* Szűrők */}
      <div style={card}>
        <div style={title}>Szűrők</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <select style={sel} value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="">Nyelv: mind</option>
            {langOptions.filter((l) => l !== '?').map((l) => <option key={l} value={l}>{hu(l)}</option>)}
          </select>
          <input style={{ ...inp, width: 130 }} placeholder="Profil…" value={profile} onChange={(e) => setProfile(e.target.value)} />
          <select style={sel} value={openerStyle} onChange={(e) => setOpenerStyle(e.target.value)}>
            <option value="">Nyitás: mind</option>
            {OPENERS.map((o) => <option key={o} value={o}>{hu(o)}</option>)}
          </select>
          <select style={sel} value={wikiQA} onChange={(e) => setWikiQA(e.target.value)}>
            <option value="">Wiki forrás: mind</option>
            {WIKIQA.map((o) => <option key={o} value={o}>{hu(o)}</option>)}
          </select>
          <select style={sel} value={promptPath} onChange={(e) => setPromptPath(e.target.value)}>
            <option value="">Prompt út: mind</option>
            {PROMPTS.map((o) => <option key={o} value={o}>{hu(o)}</option>)}
          </select>
          <select style={sel} value={geo} onChange={(e) => setGeo(e.target.value)}>
            <option value="">Geo: mind</option>
            {GEO.map((o) => <option key={o} value={o}>{hu(o)}</option>)}
          </select>
          <select style={sel} value={geoB1} onChange={(e) => setGeoB1(e.target.value)}
            title="A koordináta-alapú geo-blokk B1 (Wikidata) kimenete">
            <option value="">B1 ok: mind</option>
            {GEO_B1_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input style={inp} type="date" value={from} onChange={(e) => setFrom(e.target.value)} title="Dátumtól (Budapest)" />
          <input style={inp} type="date" value={to} onChange={(e) => setTo(e.target.value)} title="Dátumig (Budapest)" />
          <input style={{ ...inp, width: 180 }} placeholder="POI név keresés…" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
          <button style={btn} onClick={load} disabled={loading}>{loading ? 'Betöltés…' : 'Szűrés'}</button>
          <button style={{ ...btn, background: '#22c55e' }} onClick={exportXlsx} disabled={exporting || loading}
            title="Az aktuális szűrés exportja Excelbe (max. 2000 sor)">
            {exporting ? 'Exportálás…' : '📥 Excel export'}
          </button>
          <button style={{ ...btn, background: '#f59e0b' }} onClick={exportTrace} disabled={exportingTrace || loading}
            title="A szűrt narrációk teljes (szűretlen) logjai Excelben — dátumszűrő kötelező, max. 50 narráció / 7 nap">
            {exportingTrace ? 'Trace export…' : '🔍 Trace export'}
          </button>
          <button style={{ ...btn, background: '#6366f1' }} onClick={exportFull} disabled={exportingFull || loading}
            title="Egyesített elemző export egy .xlsx-ben: Összefoglaló + Narrációk + POI minőség + Napi + Trace (Trace csak ≤7 nap dátumszűrővel)">
            {exportingFull ? 'Elemző export…' : '📦 Elemző export'}
          </button>
        </div>
      </div>

      {error && <div style={{ ...card, borderColor: '#f43f5e', color: '#f43f5e', fontSize: 13 }}>Hiba: {error}</div>}

      {/* Összesítő */}
      {stats && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ ...card, minWidth: 160 }}>
            <div style={title}>Találat</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#00d4ff' }}>{stats.count}</div>
            <div style={{ fontSize: 11, color: '#8b8fa3' }}>
              {stats.partialCount > 0 && <>ebből partial: {stats.partialCount}<br /></>}
              {stats.truncated && <span style={{ color: '#f59e0b' }}>⚠️ első {stats.scanned} doc alapján</span>}
            </div>
          </div>
          <Dist label="Nyitó stílus" dist={stats.openerDist} colors={OPENER_COLORS} total={stats.count} />
          <Dist label="Wiki forrás" dist={stats.wikiDist} colors={WIKI_COLORS} total={stats.count} />
          <Dist label="Prompt út" dist={stats.promptDist} colors={PROMPT_COLORS} total={stats.count} labelOf={promptLabel} />
        </div>
      )}

      {/* Lista + részletnézet */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ ...card, flex: '1 1 420px', padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 24, color: '#8b8fa3', fontSize: 13 }}>Betöltés…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: 24, color: '#8b8fa3', fontSize: 13 }}>Nincs találat a szűrésre.</div>
          ) : (
            items.map((it) => (
              <div key={it.id} onClick={() => openDetail(it)}
                style={{ padding: '10px 14px', borderBottom: '1px solid #2a2e3a', cursor: 'pointer', background: selected === it.id ? 'rgba(0,212,255,0.08)' : 'transparent' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ color: '#e4e6eb', fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.poiName} {it.partial && <span title="megszakadt narráció" style={{ color: '#f59e0b' }}>⚠️</span>}
                  </span>
                  <span style={{ color: '#8b8fa3', fontSize: 11, flexShrink: 0 }}>{fmtTime(it.createdAt)}</span>
                </div>
                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <Badge text={hu(it.lang)} color="#00d4ff" />
                  {it.profile && <Badge text={hu(it.profile)} color="#a78bfa" />}
                  <Badge text={hu(it.openerStyle)} color={OPENER_COLORS[it.openerStyle || 'null'] || '#8b8fa3'} />
                  <Badge text={`wiki: ${hu(it.wikiQA)}`} color={WIKI_COLORS[it.wikiQA || 'null'] || '#8b8fa3'} />
                  {it.promptPath && <Badge text={`prompt: ${hu(it.promptPath)}`} color={PROMPT_COLORS[it.promptPath] || '#8b8fa3'} />}
                  {srcList(it).map((sn) => <Badge key={sn} text={hu(sn)} color={SRC_COLORS[sn] || '#8b8fa3'} />)}
                  {it.geo && <Badge text={`geo: ${hu(it.geo)}`} color="#22c55e" />}
                  <span style={{ color: '#8b8fa3', fontSize: 11, marginLeft: 'auto' }}>{it.textChars} kar.</span>
                </div>
              </div>
            ))
          )}
          {hasMore && !loading && (
            <div style={{ padding: 12, textAlign: 'center' }}>
              <button style={{ ...btn, background: '#2a2e3a', color: '#e4e6eb' }} onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Betöltés…' : 'Továbbiak betöltése'}
              </button>
            </div>
          )}
        </div>

        {/* Olvasó panel */}
        <div style={{ ...card, flex: '1 1 480px', position: 'sticky', top: 12, maxHeight: 'calc(100vh - 60px)', overflowY: 'auto' }}>
          <div style={title}>Narráció</div>
          {!selected ? (
            <div style={{ color: '#8b8fa3', fontSize: 13 }}>Kattints egy elemre a listában a teljes szöveghez.</div>
          ) : detailLoading ? (
            <div style={{ color: '#8b8fa3', fontSize: 13 }}>Betöltés…</div>
          ) : detail?.error ? (
            <div style={{ color: '#f43f5e', fontSize: 13 }}>Hiba: {detail.error}</div>
          ) : detail ? (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#e4e6eb', marginBottom: 4 }}>{detail.poiName || '—'}</div>
              <div style={{ fontSize: 12, color: '#8b8fa3', marginBottom: 8 }}>
                {fmtTime(detail.createdAt)} · {hu(detail.lang)} · {hu(detail.profile)} · {hu(detail.narrationLength)} · {hu(detail.travelMode)}
                {detail.lat != null && detail.lon != null && (
                  <> · <a href={`https://www.google.com/maps?q=${detail.lat},${detail.lon}`} target="_blank" rel="noreferrer" style={{ color: '#00d4ff' }}>{Number(detail.lat).toFixed(4)}, {Number(detail.lon).toFixed(4)}</a></>
                )}
              </div>
              <div style={{ marginBottom: 10 }}>
                <Badge text={hu(detail.openerStyle)} color={OPENER_COLORS[detail.openerStyle || 'null'] || '#8b8fa3'} />
                <Badge text={`wiki: ${hu(detail.wikiQA)}`} color={WIKI_COLORS[detail.wikiQA || 'null'] || '#8b8fa3'} />
                {detail.promptPath && <Badge text={`prompt: ${hu(detail.promptPath)}`} color={PROMPT_COLORS[detail.promptPath] || '#8b8fa3'} />}
                {srcList(detail).map((sn) => <Badge key={sn} text={hu(sn)} color={SRC_COLORS[sn] || '#8b8fa3'} />)}
                {detail.geo && <Badge text={`geo: ${hu(detail.geo)}`} color="#22c55e" />}
                {detail.partial && <Badge text="megszakadt" color="#f59e0b" />}
                {detail.modelUsed && <Badge text={`${detail.modelUsed}${detail.modelVariant ? '/' + detail.modelVariant : ''}`} color="#8b8fa3" />}
                {detail.appVersion && <Badge text={`v${detail.appVersion}`} color="#8b8fa3" />}
                {/* Tényanyag három mezőből (az app prompt_builder.js alapján):
                      clientFactsChars — az app által a kéréssel küldött VERIFIED FACTS
                      wikiQAChars      — a Wiki Q&A-ból a promptba került szöveg (vágás és
                                         névcsere után); 0 = lefutott, de nem került be semmi;
                                         null = a lekérdezés nem futott le
                      factsChars       — ÖSSZESÍTETT: a fentiek + minden szerveroldali blokk
                                         fejlécekkel; NEM egyenlő az előző kettő összegével
                    A HIÁNYZÓ mező régi mintát jelent („nincs adat"), ami a wikiQAChars-nál
                    nem ugyanaz, mint a null („nem futott"). */}
                <Badge text={`tények (kliens): ${factsText(detail, 'clientFactsChars')}`} color="#8b8fa3"
                  title="Az app által a kéréssel küldött ellenőrzött tények (VERIFIED FACTS) hossza. A szerveroldali források nincsenek benne." />
                <Badge text={`tények (wiki): ${factsText(detail, 'wikiQAChars', 'nem futott')}`} color="#8b8fa3"
                  title="A Wikipedia Q&A-ból a promptba került szöveg hossza (vágás és névcsere után). 0 = a lekérdezés lefutott, de nem került be semmi (nincs találat, elutasítva, csak város-cikk). „nem futott” = a lekérdezés nem futott le (hiba vagy időbüdzsé)." />
                <Badge text={`prompt-tények összesen: ${factsText(detail, 'factsChars')}`} color="#8b8fa3"
                  title="A promptba került összes tényanyag: kliens-tények + minden szerveroldali blokk (Wiki Q&A, Wikidata-cikk, műemlék-nyilvántartás, célnyelvi név, város/környék) a fejlécekkel, RELATED, WIKIVOYAGE és OSM-sorok. A strukturált Wikidata-tényblokk nincs benne. Rövid promptnál 0. NEM egyenlő a kliens- és a wiki-tények összegével." />
              </div>
              <GeoPaths d={detail} />
              <div style={{ fontSize: 14, lineHeight: 1.7, color: '#e4e6eb', whiteSpace: 'pre-wrap', background: '#0a0d14', border: '1px solid #2a2e3a', borderRadius: 8, padding: 14 }}>
                {detail.text || '(üres szöveg)'}
              </div>
              <div style={{ marginTop: 12 }}>
                {!trace && (
                  <button style={{ ...btn, background: '#2a2e3a', color: '#e4e6eb' }} onClick={loadTrace} disabled={traceLoading}>
                    {traceLoading ? 'Logok betöltése…' : '🔍 Pipeline trace'}
                  </button>
                )}
                {trace?.error && <div style={{ color: '#f43f5e', fontSize: 12, marginTop: 8 }}>Trace hiba: {trace.error}</div>}
                {trace && !trace.error && (
                  <div>
                    <div style={{ ...title, marginTop: 4 }}>Pipeline trace · {trace.count} logsor</div>
                    {trace.count === 0 ? (
                      <div style={{ color: '#8b8fa3', fontSize: 12 }}>Nincs találat az időablakban (a logok 30 nap után törlődnek).</div>
                    ) : (
                      <div style={{ maxHeight: 320, overflowY: 'auto', background: '#0a0d14', border: '1px solid #2a2e3a', borderRadius: 8, padding: 10 }}>
                        {trace.entries.map((t, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0', fontFamily: 'monospace', fontSize: 11 }}>
                            <span style={{ color: '#8b8fa3', flexShrink: 0 }}>{fmtTimeS(t.timestamp)}</span>
                            <span style={{ color: SEV_COLORS[t.severity] || '#8b8fa3', flexShrink: 0 }}>●</span>
                            <span style={{ color: '#e4e6eb', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{t.message}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
